import type { BoardData } from '../model/schema.js';
import { findPort, itemName } from './ports.js';
import { nodeKey } from './trace.js';
import type { NodeRef, TraceHop } from './types.js';

/** Display label of a node, e.g. `SB1 In 5`, `Ch 5 Snare`, `Mix 2 Drums`. */
export function nodeLabel(data: BoardData, node: NodeRef): string {
  switch (node.type) {
    case 'port': {
      const item = data.items[node.itemId];
      const name = item === undefined ? node.itemId : itemName(item);
      const port = findPort(data, node.itemId, node.portId)?.name ?? node.portId;
      return node.lane === undefined ? `${name} ${port}` : `${name} ${port} #${node.lane}`;
    }
    case 'item': {
      const item = data.items[node.itemId];
      return item === undefined ? node.itemId : itemName(item);
    }
    case 'channel': {
      const ch = data.channels[node.channelId];
      return ch === undefined ? node.channelId : `Ch ${ch.number} ${ch.name}`.trim();
    }
    case 'mix': {
      const mix = data.mixes[node.mixId];
      return mix === undefined ? node.mixId : `Mix ${mix.number} ${mix.name}`.trim();
    }
    case 'text':
      return node.text;
  }
}

export interface SignalPathSummary {
  /** Node labels in hop order (each node once). */
  steps: string[];
  /** True if at least one hop is informal (a gap in the formal data). */
  hasInformal: boolean;
  /** Steps joined with arrows; informal hops are drawn as `⇢`. */
  text: string;
}

/**
 * Compact, display-ready summary of a trace (see {@link traceForward}/{@link traceBackward}).
 * Pass `'backward'` for backward traces so the summary still reads upstream → downstream.
 */
export function signalPathSummary(
  data: BoardData,
  hops: readonly TraceHop[],
  direction: 'forward' | 'backward' = 'forward',
): SignalPathSummary {
  const seen = new Set<string>();
  const steps: string[] = [];
  let text = '';
  const ordered = direction === 'forward' ? hops : [...hops].reverse();
  for (const hop of ordered) {
    for (const node of [hop.from, hop.to]) {
      const key = nodeKey(node);
      if (seen.has(key)) continue;
      seen.add(key);
      const label = nodeLabel(data, node);
      steps.push(label);
      text = text === '' ? label : `${text} ${hop.kind === 'informal' ? '⇢' : '→'} ${label}`;
    }
  }
  return { steps, hasInformal: hops.some((h) => h.kind === 'informal'), text };
}
